/**
 * TodayAttendance
 * ---------------
 * Daily attendance reconciliation for owner + coaches at `/coach/today`.
 *
 * Built per Sachin spec (2026-06-10):
 *   - Tiles for scheduled / attended / missed / late-cancel / mismatches
 *   - Per-row: athlete name + slot label + status + "8 → 7" from/to + ✓ if
 *     the live remaining matches the math
 *   - Mismatch banner at top if any rows are flagged
 *   - Timezone toggle CT ↔ PT (default CT, anchored to Lubbock)
 *   - Auto-refreshes every 30 seconds
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
    ArrowLeft, Loader2, RefreshCw, Clock, AlertTriangle,
    CheckCircle2, XCircle, MinusCircle, User as UserIcon,
    ChevronDown, ClipboardList, CalendarClock, ArrowRight,
    Ghost,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import AthleteNameLink from "@/components/coach/AthleteNameLink";
import OpsTabs from "@/components/coach/OpsTabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TZ_OPTIONS = [
    { v: "America/Chicago", label: "CT · Lubbock" },
    { v: "America/Los_Angeles", label: "PT · Pacific" },
];

const STATUS_META = {
    attended:    { label: "ATTENDED",    color: "text-emerald-300 border-emerald-700/60 bg-emerald-900/20",  icon: CheckCircle2 },
    booked:      { label: "SCHEDULED",   color: "text-zinc-200 border-zinc-700 bg-zinc-900/40",              icon: Clock },
    missed:      { label: "MISSED",      color: "text-amber-200 border-amber-700/60 bg-amber-950/30",        icon: XCircle },
    late_cancel: { label: "LATE CANCEL", color: "text-orange-200 border-orange-700/60 bg-orange-950/30",     icon: MinusCircle },
    cancelled:   { label: "CANCELLED",   color: "text-zinc-400 border-zinc-700 bg-zinc-900/30",              icon: MinusCircle },
};

const fmtTime = (t) => {
    if (!t) return "—";
    try {
        const [h, m] = t.split(":");
        const hh = parseInt(h, 10);
        const ap = hh >= 12 ? "PM" : "AM";
        const h12 = ((hh + 11) % 12) + 1;
        return `${h12}:${m} ${ap}`;
    } catch { return t; }
};
const fmtCheckedInAt = (iso, tz) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleTimeString(undefined, {
            hour: "numeric", minute: "2-digit", timeZone: tz,
        });
    } catch { return iso; }
};

export default function TodayAttendance() {
    const { user, loading: authLoading } = useAuth();
    const [tz] = useState("America/Los_Angeles");  // Fix 4: locked to business tz
    const [selectedDate, setSelectedDate] = useState(""); // "" = today
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const timerRef = useRef(null);

    const fetchToday = useCallback(async (silent = false) => {
        if (!silent) setRefreshing(true);
        try {
            const dateParam = selectedDate ? `&date=${selectedDate}` : "";
            const r = await axios.get(`${API}/coach/attendance/today?tz=${tz}${dateParam}`, { withCredentials: true });
            setData(r.data);
        } catch (e) {
            if (!silent) toast.error("Couldn't load attendance", {
                description: e?.response?.data?.detail || "Try refreshing.",
            });
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [tz, selectedDate]);

    useEffect(() => {
        Promise.resolve().then(() => fetchToday());
        // Auto-refresh every 30s for live "today" view only; when reviewing a
        // past day for no-show triage, the data is static so polling is wasted.
        if (!selectedDate) {
            timerRef.current = setInterval(() => fetchToday(true), 30000);
        }
        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
        };
    }, [fetchToday, selectedDate]);

    if (authLoading || loading) {
        return (
            <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
                <Loader2 className="w-7 h-7 text-brand-red animate-spin" />
            </div>
        );
    }
    if (!user || (user.role !== "owner" && user.role !== "coach")) {
        return (
            <div className="min-h-screen bg-zinc-950 text-zinc-300 flex items-center justify-center px-6 text-center">
                Only owners and coaches can view this page.
            </div>
        );
    }
    if (!data) return null;

    const counts = data.counts || {};
    const rows = data.rows || [];

    return (
        <main data-testid="today-attendance-page" className="min-h-screen bg-zinc-950 text-white pb-16">
            <header className="px-5 pt-7 pb-3 max-w-5xl mx-auto">
                <OpsTabs />
                <Link
                    to="/coach"
                    className="inline-flex items-center gap-1 text-[10px] tracking-[0.2em] uppercase text-zinc-400 hover:text-white mb-3"
                >
                    <ArrowLeft className="w-3 h-3" /> Back to coach
                </Link>
                <div className="flex items-end justify-between gap-3 flex-wrap">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-semibold">
                            {selectedDate ? "Attendance review" : "Today's attendance"}
                        </h1>
                        <p className="text-zinc-400 text-sm mt-1">
                            {data.date}
                            {selectedDate
                                ? " · reviewing past day — mark any no-shows below"
                                : " · auto-refreshes every 30s"}
                        </p>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        {/* Date jumper: yesterday button + native date input */}
                        <div className="flex items-center gap-0 border border-zinc-800 rounded" data-testid="attendance-date-jumper">
                            <button
                                type="button"
                                onClick={() => setSelectedDate("")}
                                data-testid="attendance-jump-today"
                                className={`px-3 py-1.5 text-[10px] tracking-[0.2em] uppercase transition-colors ${
                                    !selectedDate ? "bg-brand-red text-white" : "text-zinc-400 hover:text-white"
                                }`}
                            >
                                Today
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    const d = new Date();
                                    d.setDate(d.getDate() - 1);
                                    setSelectedDate(d.toISOString().slice(0, 10));
                                }}
                                data-testid="attendance-jump-yesterday"
                                className={`px-3 py-1.5 text-[10px] tracking-[0.2em] uppercase transition-colors ${
                                    selectedDate ? "bg-brand-red text-white" : "text-zinc-400 hover:text-white"
                                }`}
                            >
                                Yesterday
                            </button>
                            <input
                                type="date"
                                value={selectedDate || ""}
                                onChange={(e) => setSelectedDate(e.target.value)}
                                data-testid="attendance-date-input"
                                className="bg-transparent text-zinc-300 text-[11px] px-2 py-1.5 border-l border-zinc-800 outline-none"
                                max={new Date().toISOString().slice(0, 10)}
                            />
                        </div>
                        <div className="px-3 py-1.5 text-[10px] tracking-[0.2em] uppercase text-zinc-400 border border-zinc-800 rounded" data-testid="tz-locked-pacific">
                            PT · Pacific
                        </div>
                        <button
                            type="button"
                            onClick={() => fetchToday()}
                            disabled={refreshing}
                            data-testid="refresh-today"
                            className="border border-zinc-800 rounded p-2 hover:border-zinc-600 disabled:opacity-50 transition-colors"
                            title="Refresh now"
                        >
                            <RefreshCw className={`w-3.5 h-3.5 text-zinc-400 ${refreshing ? "animate-spin" : ""}`} />
                        </button>
                    </div>
                </div>
            </header>

            <div className="max-w-5xl mx-auto px-5">
                {/* Mismatch banner */}
                {counts.mismatches > 0 && (
                    <MismatchBanner
                        count={counts.mismatches}
                        rows={rows.filter((r) => r.mismatch)}
                        dateParam={selectedDate}
                        onResolved={() => fetchToday()}
                    />
                )}

                {/* Orphaned-booking banner — bookings on cancelled or deleted
                    slots that would otherwise ghost from this view. Sachin
                    asked for this on 2026-02-15: "Make sure they ALWAYS
                    pull when someone is in." */}
                {data.orphans?.length > 0 && (
                    <OrphanBanner orphans={data.orphans} onRefresh={fetchToday} />
                )}

                {/* Tiles */}
                <section
                    data-testid="today-tiles"
                    className="grid grid-cols-2 md:grid-cols-4 gap-3 my-4"
                >
                    <Tile label="Scheduled" value={counts.scheduled} testid="tile-scheduled" tone="zinc" />
                    <Tile label="Attended"  value={counts.attended}  testid="tile-attended"  tone="emerald" />
                    <Tile label="Missed"    value={counts.missed}    testid="tile-missed"    tone="amber" />
                    <Tile label="Late cancel" value={counts.late_cancel} testid="tile-late-cancel" tone="orange" />
                </section>

                {/* Rows — grouped into collapsible sections */}
                {(() => {
                    // Rescheduled-away rows live in their own section, not the main lists.
                    // 2026-08: deleted attendance ('cancelled_after_checkin') must
                    // vanish from the list in the same refresh — it was showing up
                    // under Scheduled after a delete.
                    const active = rows.filter((r) =>
                        r.status !== "cancelled_after_checkin"
                        && !(r.status === "cancelled" && r.cancel_reason === "rescheduled"));
                    const assessments = active.filter((r) => r.is_assessment);
                    const scheduled = active.filter((r) => !r.is_assessment && r.status !== "attended");
                    const came = active.filter((r) => !r.is_assessment && r.status === "attended");
                    const rescheduled = data.rescheduled || [];
                    return (
                        <div className="space-y-3">
                            <Section title="Assessments" count={assessments.length} testid="section-assessments" icon={ClipboardList}>
                                {assessments.length === 0
                                    ? <EmptyNote text="No assessments today." />
                                    : assessments.map((r) => <Row key={r.booking_id} row={r} tz={tz} onChanged={fetchToday} />)}
                            </Section>
                            <Section title="Scheduled for today" count={scheduled.length} testid="section-scheduled" icon={Clock}>
                                {scheduled.length === 0
                                    ? <EmptyNote text="Nothing left on the schedule." />
                                    : scheduled.map((r) => <Row key={r.booking_id} row={r} tz={tz} onChanged={fetchToday} />)}
                            </Section>
                            <Section title="Came today" count={came.length} testid="section-came" icon={CheckCircle2}>
                                {came.length === 0
                                    ? <EmptyNote text="No check-ins yet." />
                                    : came.map((r) => <Row key={r.booking_id} row={r} tz={tz} onChanged={fetchToday} />)}
                            </Section>
                            <Section title="Rescheduled" count={rescheduled.length} testid="section-rescheduled" icon={CalendarClock}>
                                {rescheduled.length === 0
                                    ? <EmptyNote text="No one moved their session today." />
                                    : rescheduled.map((r) => <RescheduledRow key={r.booking_id} r={r} />)}
                            </Section>
                        </div>
                    );
                })()}

                <p className="text-[11px] text-zinc-500 mt-6 text-center">
                    Showing {rows.length} bookings · last refresh {new Date().toLocaleTimeString(undefined, { timeZone: tz })}
                </p>
            </div>
        </main>
    );
}


/* ─────────────────────────── Sub-components ────────────────────── */

function Section({ title, count, testid, icon: Icon, children }) {
    const [open, setOpen] = useState(count > 0);
    return (
        <section data-testid={testid} className="border border-zinc-800 rounded-lg overflow-hidden">
            <button
                type="button"
                data-testid={`${testid}-toggle`}
                onClick={() => setOpen((o) => !o)}
                className="w-full flex items-center justify-between gap-3 px-4 py-3 bg-zinc-900/60 hover:bg-zinc-900 transition-colors"
            >
                <span className="inline-flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase text-zinc-200">
                    {Icon && <Icon className="w-3.5 h-3.5 text-brand-red" />} {title}
                </span>
                <span className="flex items-center gap-2.5">
                    <span className="text-sm font-bold text-white" data-testid={`${testid}-count`}>{count}</span>
                    <ChevronDown className={`w-4 h-4 text-zinc-400 transition-transform ${open ? "rotate-180" : ""}`} />
                </span>
            </button>
            {open && <div className="p-3 space-y-2">{children}</div>}
        </section>
    );
}

function EmptyNote({ text }) {
    return <div className="text-center py-4 text-xs text-zinc-500">{text}</div>;
}

function RescheduledRow({ r }) {
    const fmtSlot = (s) => {
        if (!s || !s.date) return "—";
        const d = new Date(`${s.date}T00:00:00`).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
        return `${d} · ${fmtTime(s.start_time)}`;
    };
    return (
        <div
            data-testid={`rescheduled-row-${r.booking_id}`}
            className="border border-sky-700/40 bg-sky-950/20 rounded-lg px-4 py-3"
        >
            <div className="flex items-center gap-2 flex-wrap">
                <AthleteNameLink athleteId={r.athlete_id} name={r.athlete_name} testid={`rescheduled-name-${r.booking_id}`} className="text-sm font-semibold text-white truncate hover:text-brand-red underline-offset-2 hover:underline transition-colors" />
                {r.is_assessment && (
                    <span className="text-[9px] tracking-[0.2em] uppercase border border-sky-600/50 text-sky-300 px-1.5 py-0.5 rounded">Assessment</span>
                )}
            </div>
            <div className="text-xs text-zinc-300 mt-1.5 flex items-center gap-2 flex-wrap">
                <span className="opacity-70">{fmtSlot(r.from)}</span>
                <ArrowRight className="w-3.5 h-3.5 text-sky-300" />
                {r.to ? (
                    <span className="font-semibold text-white">{fmtSlot(r.to)}</span>
                ) : (
                    <span className="text-amber-300">not rebooked yet</span>
                )}
            </div>
        </div>
    );
}

function Tile({ label, value, testid, tone }) {
    const toneCls = {
        zinc: "border-zinc-800 bg-zinc-900/40 text-white",
        emerald: "border-emerald-700/40 bg-emerald-900/15 text-emerald-300",
        amber: "border-amber-700/40 bg-amber-950/30 text-amber-200",
        orange: "border-orange-700/40 bg-orange-950/30 text-orange-200",
    }[tone] || "border-zinc-800 bg-zinc-900/40 text-white";
    return (
        <div data-testid={testid} className={`border rounded-lg px-4 py-3 ${toneCls}`}>
            <div className="text-[10px] tracking-[0.25em] uppercase opacity-80">{label}</div>
            <div className="text-3xl font-bold leading-none mt-1">{value ?? 0}</div>
        </div>
    );
}

function OrphanBanner({ orphans, onRefresh }) {
    const [busyId, setBusyId] = useState(null);

    const checkIn = (o) => {
        // No surviving slot to attach to — direct the coach to the athlete's
        // profile where they can log the session manually. Quick instruction
        // via toast is enough; one-tap fix can come later if needed.
        toast.info(
            `Open ${o.athlete_name}'s profile to log this session manually — the original slot was ${o.reason.toLowerCase()}.`,
            { duration: 6000 },
        );
    };

    const cancelCleanly = async (o) => {
        if (busyId) return;
        setBusyId(o.booking_id);
        try {
            await axios.delete(`${API}/schedule/bookings/${o.booking_id}`, { withCredentials: true });
            toast.success("Booking cleared.");
            onRefresh && onRefresh(true);
        } catch (e) {
            toast.error("Couldn't clear booking", { description: e?.response?.data?.detail });
        } finally {
            setBusyId(null);
        }
    };

    return (
        <section
            data-testid="orphan-banner"
            className="border border-amber-700/60 bg-amber-950/30 rounded-lg px-4 py-3 my-3"
        >
            <div className="flex items-start gap-3">
                <Ghost className="w-4 h-4 text-amber-300 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-amber-100">
                        {orphans.length} orphan booking{orphans.length === 1 ? "" : "s"} on cancelled or deleted slots
                    </div>
                    <div className="text-[11px] text-amber-200/80 mt-1">
                        These athletes have a booking for today but their slot is gone (class was
                        cancelled or removed). Check them in manually if they showed up, or clear
                        the booking to stop it from haunting the dashboard.
                    </div>
                    <ul className="mt-2 space-y-1.5">
                        {orphans.slice(0, 8).map((o) => (
                            <li
                                key={o.booking_id}
                                data-testid={`orphan-row-${o.booking_id}`}
                                className="text-xs text-amber-100 flex items-center justify-between gap-3 flex-wrap"
                            >
                                <span className="truncate">
                                    • {o.athlete_name} — {o.label} {fmtTime(o.start_time)} <span className="opacity-60">({o.reason})</span>
                                </span>
                                <span className="flex items-center gap-2 flex-shrink-0">
                                    <button
                                        type="button"
                                        data-testid={`orphan-checkin-${o.booking_id}`}
                                        onClick={() => checkIn(o)}
                                        className="text-[10px] tracking-[0.18em] uppercase border border-emerald-500/60 text-emerald-200 px-2 py-0.5 rounded hover:bg-emerald-500/15"
                                    >
                                        Check in
                                    </button>
                                    <button
                                        type="button"
                                        data-testid={`orphan-clear-${o.booking_id}`}
                                        onClick={() => cancelCleanly(o)}
                                        disabled={busyId === o.booking_id}
                                        className="text-[10px] tracking-[0.18em] uppercase border border-amber-500/60 text-amber-200 px-2 py-0.5 rounded hover:bg-amber-500/15 disabled:opacity-50"
                                    >
                                        Clear
                                    </button>
                                </span>
                            </li>
                        ))}
                        {orphans.length > 8 && (
                            <li className="text-[11px] text-amber-300/70">+ {orphans.length - 8} more</li>
                        )}
                    </ul>
                </div>
            </div>
        </section>
    );
}


function MismatchBanner({ count, rows, dateParam, onResolved }) {
    const [busy, setBusy] = useState(false);
    const handleResolve = async () => {
        const ok = window.confirm(
            `Resolve all ${count} unreconciled attendance${count === 1 ? "" : "s"}?\n\n` +
            `For each row, this will:\n` +
            `  • Find the most recent active pack on that athlete's account and deduct one session\n` +
            `  • If no active pack exists, open an IOU for the coach to resolve later\n\n` +
            `Already-reconciled rows are skipped. Audit row tagged "bulk_reconcile" is written.`
        );
        if (!ok) return;
        setBusy(true);
        try {
            const qs = dateParam ? `?date=${dateParam}` : "";
            const r = await axios.post(`${API}/coach/attendance/bulk-reconcile${qs}`, null, { withCredentials: true });
            const { fixed, iou_created, skipped } = r.data || {};
            toast.success("Bulk reconcile complete", {
                description: `Fixed ${fixed} · ${iou_created} IOUs opened · ${skipped} skipped (already reconciled)`,
            });
            onResolved?.();
        } catch (e) {
            toast.error("Couldn't bulk reconcile", {
                description: e?.response?.data?.detail || "Try again or fix rows individually.",
            });
        } finally {
            setBusy(false);
        }
    };
    return (
        <section
            data-testid="mismatch-banner"
            className="border border-rose-700/60 bg-rose-950/40 rounded-lg px-4 py-3 my-3"
        >
            <div className="flex items-start gap-3">
                <AlertTriangle className="w-4 h-4 text-rose-300 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div>
                            <div className="text-sm font-medium text-rose-100">
                                {count} reconciliation issue{count === 1 ? "" : "s"} today
                            </div>
                            <div className="text-[11px] text-rose-200/80 mt-1">
                                These athletes were marked attended but no session was deducted from any pack —
                                the deduction silently failed. Fix all in one click, or resolve each manually.
                            </div>
                        </div>
                        <button
                            type="button"
                            data-testid="bulk-reconcile-btn"
                            onClick={handleResolve}
                            disabled={busy}
                            className="text-[10px] tracking-[0.2em] uppercase px-3 py-1.5 bg-rose-600 hover:bg-rose-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded shrink-0"
                        >
                            {busy ? "Resolving…" : "Resolve all"}
                        </button>
                    </div>
                    <ul className="mt-2 space-y-1">
                        {rows.slice(0, 5).map((r) => (
                            <li key={r.booking_id} className="text-xs text-rose-200">
                                • {r.athlete_first_name} {r.athlete_last_name} — {r.label} {fmtTime(r.start_time)}
                            </li>
                        ))}
                        {rows.length > 5 && (
                            <li className="text-[11px] text-rose-300/70">+ {rows.length - 5} more</li>
                        )}
                    </ul>
                </div>
            </div>
        </section>
    );
}

function Row({ row, tz, onChanged }) {
    const meta = STATUS_META[row.status] || STATUS_META.booked;
    const Icon = meta.icon;
    const fullName = `${row.athlete_first_name} ${row.athlete_last_name || ""}`.trim();
    const showChain = row.sessions_before != null && row.sessions_after != null;
    const liveMatches = showChain && row.live_remaining != null;
    const canCancel = row.status === "booked" || row.status === "scheduled";
    // 2026-08 (Sachin): every attendance record must be deletable, no matter
    // how it was logged. Unified backend flow handles all shapes + refund.
    const hasAttendance = row.status === "checked_in" || row.status === "attended"
        || !!row.checked_in_at || !!row.marked_attended_at;

    const handleDeleteAttendance = async () => {
        if (!window.confirm(`Delete ${fullName}'s attendance record?`)) return;
        const creditBack = window.confirm(
            "Credit the session back to their pack?\n\n" +
            "OK = credit it back (most common — entered by mistake).\n" +
            "Cancel = leave the pack as-is.",
        );
        try {
            const r = await axios.delete(
                `${API}/coach/attendance/record/${row.booking_id}?credit_back=${creditBack}`,
                { withCredentials: true },
            );
            const refund = r.data?.refunded_from;
            toast.success("Attendance deleted", {
                description: creditBack
                    ? (refund
                        ? `1 session credited back to ${refund.label} · ${refund.remaining} left.`
                        : "No matching deduction found — nothing to credit back.")
                    : "Pack untouched.",
            });
            onChanged?.();
        } catch (e) {
            toast.error("Couldn't delete", { description: e?.response?.data?.detail || "Try again." });
        }
    };

    const handleCancel = async () => {
        const reason = window.prompt(`Cancel ${fullName}'s booking?\n\nOptional note (e.g. "parent texted, kid sick"). Press OK to cancel even if blank, or Cancel to abort.`);
        if (reason === null) return;
        try {
            const r = await axios.post(
                `${API}/coach/bookings/${row.booking_id}/cancel`,
                { reason: reason || undefined },
                { withCredentials: true },
            );
            const status = r.data?.new_status || "cancelled";
            toast.success(`${fullName} cancelled`, { description: status.replace("_", " ") });
            onChanged?.();
        } catch (e) {
            toast.error("Couldn't cancel", { description: e?.response?.data?.detail || "Try again." });
        }
    };

    return (
        <div
            data-testid={`attendance-row-${row.booking_id}`}
            className={`border rounded-lg px-4 py-3 ${meta.color}`}
        >
            <div className="flex items-start gap-3">
                <Icon className="w-4 h-4 mt-0.5 flex-shrink-0 opacity-80" />
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <AthleteNameLink athleteId={row.athlete_id} name={fullName} testid={`attendance-name-${row.booking_id}`} className="text-sm font-semibold text-white truncate hover:text-brand-red underline-offset-2 hover:underline transition-colors" />
                        <span className="text-[10px] tracking-[0.2em] uppercase opacity-80">{meta.label}</span>
                        {row.is_assessment && (
                            <span className="text-[9px] tracking-[0.2em] uppercase border border-sky-600/50 text-sky-300 px-1.5 py-0.5 rounded">Assessment</span>
                        )}
                        {row.mismatch && (
                            <span data-testid={`mismatch-${row.booking_id}`} className="text-[10px] tracking-[0.2em] uppercase border border-rose-500 text-rose-200 px-1.5 py-0.5 rounded">
                                ⚠ NO DEDUCTION
                            </span>
                        )}
                    </div>
                    <div className="text-xs opacity-75 mt-0.5 flex items-center gap-2 flex-wrap">
                        <span>{row.label}</span>
                        <span>· {fmtTime(row.start_time)}</span>
                        {row.parent_first_name && row.parent_first_name !== "—" && (
                            <span className="inline-flex items-center gap-1 opacity-80">
                                <UserIcon className="w-3 h-3" />{row.parent_first_name}
                            </span>
                        )}
                        {row.is_manual_log && (
                            <span className="text-[10px] uppercase tracking-wider opacity-70">· manual log</span>
                        )}
                    </div>
                </div>
                <div className="text-right flex-shrink-0">
                    {showChain ? (
                        <div data-testid={`from-to-${row.booking_id}`} className="text-sm font-mono">
                            <span className="opacity-60">{row.sessions_before}</span>
                            <span className="opacity-50 mx-1">→</span>
                            <span className="font-bold text-white">{row.sessions_after}</span>
                            {liveMatches && (
                                <CheckCircle2 className="w-3 h-3 text-emerald-400 inline ml-1.5" title="Matches live profile balance" />
                            )}
                        </div>
                    ) : (
                        row.live_remaining != null && (
                            <div className="text-xs opacity-70">
                                {row.live_remaining} left
                            </div>
                        )
                    )}
                    {/* Show which pack/credit the session was pulled from so
                        Sachin can verify the deduction matches the athlete's
                        profile at a glance. (2026-02-15.) Only renders when
                        the deduction reconciled. The from→to chain above
                        already proves the math; this names the source. */}
                    {row.deduction && (
                        <div
                            data-testid={`deduction-${row.booking_id}`}
                            className="text-[10px] opacity-70 mt-0.5 text-right"
                            title={`Pulled from ${row.deduction.pack_label} (${row.deduction.source})`}
                        >
                            ↳ {row.deduction.pack_label}
                        </div>
                    )}
                    {row.checked_in_at && (
                        <div className="text-[10px] opacity-60 mt-0.5">
                            in @ {fmtCheckedInAt(row.checked_in_at, tz)}
                            {row.checked_in_by_name && <span> · {row.checked_in_by_name}</span>}
                        </div>
                    )}
                    {row.cancelled_at && (
                        <div data-testid={`cancelled-at-${row.booking_id}`} className="text-[10px] text-zinc-500 mt-0.5"
                            title={`Cancelled ${fmtCheckedInAt(row.cancelled_at, tz)}${row.cancelled_by_name ? ` by ${row.cancelled_by_name}` : ""}`}>
                            cancelled @ {fmtCheckedInAt(row.cancelled_at, tz)}
                            {row.cancelled_by_name && <span> · {row.cancelled_by_name}</span>}
                        </div>
                    )}
                    {canCancel && (
                        <button
                            type="button"
                            data-testid={`cancel-booking-${row.booking_id}`}
                            onClick={handleCancel}
                            className="mt-1.5 text-[10px] tracking-[0.2em] uppercase text-zinc-400 hover:text-rose-300 underline-offset-2 hover:underline"
                            title="Cancel this booking — keeps a record, doesn't deduct a session."
                        >
                            cancel
                        </button>
                    )}
                    {row.is_assessment && (
                        <div className="mt-1.5" data-testid={`trial-outcome-wrap-${row.booking_id}`}>
                            <select
                                data-testid={`trial-outcome-${row.booking_id}`}
                                defaultValue={row.assessment_outcome || "unrecorded"}
                                onChange={async (e) => {
                                    const v = e.target.value;
                                    if (v === "unrecorded") return;
                                    try {
                                        await axios.post(`${API}/coach/assessments/${row.booking_id}/outcome`,
                                            { outcome: v }, { withCredentials: true });
                                        toast.success("Trial outcome saved");
                                        onChanged?.();
                                    } catch (err) {
                                        toast.error("Couldn't save outcome", { description: err?.response?.data?.detail });
                                    }
                                }}
                                className="bg-zinc-900 border border-zinc-700 text-[11px] text-zinc-200 rounded px-2 py-1 outline-none"
                            >
                                <option value="unrecorded">Outcome…</option>
                                <option value="attended_converted">Attended – converted</option>
                                <option value="attended_not_converted">Attended – not converted</option>
                                <option value="no_show">No-show</option>
                                <option value="cancelled">Cancelled</option>
                                <option value="rescheduled">Rescheduled</option>
                            </select>
                        </div>
                    )}
                    {hasAttendance && (
                        <button
                            type="button"
                            data-testid={`delete-attendance-${row.booking_id}`}
                            onClick={handleDeleteAttendance}
                            className="mt-1.5 text-[10px] tracking-[0.2em] uppercase text-zinc-500 hover:text-rose-300 underline-offset-2 hover:underline"
                            title="Delete this attendance record — optional session refund."
                        >
                            delete
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}


/* ─────────────────────────── Dashboard tile (exported) ──────────────────── */

/**
 * Compact summary tile for CoachDashboard.jsx — shows the 4 daily counts +
 * a link to the full page. Self-fetches on mount + auto-refreshes every 30s.
 */
export function TodayAttendanceTile() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const timerRef = useRef(null);

    const fetch = useCallback(async (silent = false) => {
        try {
            const r = await axios.get(`${API}/coach/attendance/today?tz=America/Chicago`, { withCredentials: true });
            setData(r.data);
        } catch (e) {
            if (!silent) console.warn("today-tile fetch failed", e);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        Promise.resolve().then(() => fetch());
        timerRef.current = setInterval(() => fetch(true), 30000);
        return () => clearInterval(timerRef.current);
    }, [fetch]);

    const counts = useMemo(() => data?.counts || {}, [data]);

    if (loading) {
        return (
            <Link
                to="/coach/today"
                data-testid="today-tile-loading"
                className="block border border-zinc-800 rounded-lg px-4 py-3 bg-zinc-900/40 animate-pulse"
            >
                <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500">Today</div>
                <div className="h-6 w-24 bg-zinc-800 rounded mt-1" />
            </Link>
        );
    }

    return (
        <Link
            to="/coach/today"
            data-testid="today-attendance-tile"
            className="block border border-zinc-800 rounded-lg px-4 py-3 bg-zinc-900/40 hover:border-brand-red/60 transition-colors"
        >
            <div className="flex items-center justify-between gap-3 mb-2">
                <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-400">
                    Today · {data?.date}
                </div>
                {counts.mismatches > 0 && (
                    <span className="text-[10px] tracking-[0.2em] uppercase border border-rose-500 text-rose-300 px-1.5 py-0.5 rounded">
                        {counts.mismatches} ⚠
                    </span>
                )}
            </div>
            <div className="grid grid-cols-4 gap-2">
                <TileMini label="Sched" value={counts.scheduled} testid="tile-mini-scheduled" />
                <TileMini label="Attended" value={counts.attended} testid="tile-mini-attended" tone="emerald" />
                <TileMini label="Missed" value={counts.missed} testid="tile-mini-missed" tone="amber" />
                <TileMini label="L/Cancel" value={counts.late_cancel} testid="tile-mini-late" tone="orange" />
            </div>
            <div className="text-[10px] text-zinc-500 mt-2 tracking-[0.2em] uppercase">
                Tap for reconciliation →
            </div>
        </Link>
    );
}

function TileMini({ label, value, testid, tone }) {
    const tones = {
        emerald: "text-emerald-300",
        amber: "text-amber-300",
        orange: "text-orange-300",
    };
    return (
        <div data-testid={testid} className="text-center">
            <div className={`text-2xl font-bold leading-none ${tones[tone] || "text-white"}`}>{value ?? 0}</div>
            <div className="text-[9px] tracking-[0.15em] uppercase text-zinc-500 mt-1">{label}</div>
        </div>
    );
}
